import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../AuthContext';
import { getOAuthLoginUrl, type OAuthProvider } from '../api';
import Spinner from '../components/Spinner';

declare const LOGIN_WITH_GOOGLE_ENABLED: boolean;
declare const LOGIN_WITH_GOOGLE_WORKSPACE_ENABLED: boolean;
declare const LOGIN_WITH_APPLE_ENABLED: boolean;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    const authError = sessionStorage.getItem('authError');

    if (authError) {
      setError(authError);
      sessionStorage.removeItem('authError');
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username, password);
      setError('');
    } catch (err) {
      setError('Invalid credentials');
    }
  };

  const handleOAuthLogin = (provider: OAuthProvider) => {
    window.location.assign(getOAuthLoginUrl(provider));
  };

  const showOAuthLoginButtons = LOGIN_WITH_GOOGLE_ENABLED
    || LOGIN_WITH_GOOGLE_WORKSPACE_ENABLED
    || LOGIN_WITH_APPLE_ENABLED;

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <form
        className="bg-white p-8 rounded shadow-md w-full max-w-sm"
        onSubmit={handleLogin}
        aria-label="Login form"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>
        <label htmlFor="username" className="block mb-2 font-medium">
          Username
        </label>
        <input
          id="username"
          name="username"
          type="text"
          autoComplete="username"
          className="w-full px-3 py-2 mb-4 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          aria-required="true"
        />
        <label htmlFor="password" className="block mb-2 font-medium">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          className="w-full px-3 py-2 mb-4 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          aria-required="true"
        />
        {error && <div className="text-red-600 mb-4" role="alert">{error}</div>}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Login"
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
        {showOAuthLoginButtons && (
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
            {LOGIN_WITH_GOOGLE_ENABLED && (
              <button
                type="button"
                className="w-full border border-gray-300 bg-white text-gray-800 py-2 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => handleOAuthLogin('google')}
              >
                Continue with Google
              </button>
            )}
            {LOGIN_WITH_GOOGLE_WORKSPACE_ENABLED && (
              <button
                type="button"
                className="w-full border border-blue-700 bg-blue-700 text-white py-2 rounded hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => handleOAuthLogin('google-workspace')}
              >
                Continue with Weblium Google
              </button>
            )}
            {LOGIN_WITH_APPLE_ENABLED && (
              <button
                type="button"
                className="w-full border border-gray-900 bg-gray-900 text-white py-2 rounded hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-700"
                onClick={() => handleOAuthLogin('apple')}
              >
                Continue with Apple
              </button>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default Login;
